import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronRight, Star } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  disconnectConnection,
  type IntegrationCatalogEntry,
  type IntegrationConnection,
  listIntegrationCatalog,
  listIntegrations,
  setConnectionDefault,
} from "../../../../integrations/api";
import { HttpProxiesSection } from "./-http-proxies";

export const Route = createFileRoute("/_workspace/settings/integrations/")({
  component: AppIntegrationsPane,
});

/**
 * The integrations settings pane: what's granted, to whom, and disconnecting.
 *
 * Deliberately NO "Connect" affordance for curated OAuth providers. A connect
 * from here could only request the provider's baseline identity scopes —
 * useless until an App declares the access it needs — and it would imply
 * connecting enables something by itself. Connecting happens where the need
 * arises: an App that declares an integration shows an in-app grant prompt
 * (with its scopes and its reason), and completing THAT flow lands here as a
 * granted connection. This page reviews and revokes.
 */
function AppIntegrationsPane() {
  const qc = useQueryClient();
  const catalog = useQuery({
    queryKey: ["integration-catalog"],
    queryFn: listIntegrationCatalog,
  });
  const connections = useQuery({
    queryKey: ["integrations"],
    queryFn: listIntegrations,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["integrations"] });
  const disconnect = useMutation({
    mutationFn: (connectionId: string) => disconnectConnection(connectionId),
    onSuccess: invalidate,
  });
  const makeDefault = useMutation({
    mutationFn: (args: { connectionId: string; isDefault: boolean }) =>
      setConnectionDefault(args.connectionId, args.isDefault),
    onSuccess: invalidate,
  });

  // Multi-account: a provider can carry several connections (two members'
  // Googles, or one member's personal + work) — group rows per provider.
  const connectionsByProvider = new Map<string, IntegrationConnection[]>();
  for (const c of connections.data ?? []) {
    const group = connectionsByProvider.get(c.integration) ?? [];
    group.push(c);
    connectionsByProvider.set(c.integration, group);
  }
  const connectedEntries = (catalog.data ?? []).filter((entry) =>
    connectionsByProvider.has(entry.id),
  );

  return (
    <div className="mx-auto w-full max-w-2xl px-6 pt-8 pb-12">
      <h1 className="text-lg font-semibold">App integration</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        App integrations let Apps connect to external services (e.g. Google
        Calendar) on your behalf.{" "}
        <strong className="font-medium text-foreground">
          Your credentials are never shared with the App
        </strong>
        .
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        When an App needs access to a{" "}
        <BuiltInServicesHint catalog={catalog.data} /> it will prompt you to
        connect and explain what permissions it needs. If you want to connect to
        another service, ask Heath to walk you through connecting an HTTP Proxy
        below.
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        Connections are shared with everybody in the workspace.{" "}
        <strong className="font-medium text-foreground">
          Do not connect anything you wouldn't share with everyone here.
        </strong>
      </p>

      <section className="mt-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Connected services
        </h2>
        {catalog.isLoading || connections.isLoading ? (
          <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        ) : catalog.error || connections.error ? (
          <p className="mt-2 text-sm text-destructive">
            {((catalog.error ?? connections.error) as Error).message}
          </p>
        ) : connectedEntries.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Nothing connected yet.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-border rounded-md border border-border">
            {connectedEntries.map((entry) => (
              <IntegrationRow
                key={entry.id}
                entry={entry}
                connections={connectionsByProvider.get(entry.id) ?? []}
                onDisconnect={(connectionId) => disconnect.mutate(connectionId)}
                disconnectingId={
                  disconnect.isPending ? disconnect.variables : undefined
                }
                onMakeDefault={(connectionId, isDefault) =>
                  makeDefault.mutate({ connectionId, isDefault })
                }
              />
            ))}
          </ul>
        )}
        {disconnect.error ? (
          <p className="mt-2 text-sm text-destructive">
            {(disconnect.error as Error).message}
          </p>
        ) : null}
        {makeDefault.error ? (
          <p className="mt-2 text-sm text-destructive">
            {(makeDefault.error as Error).message}
          </p>
        ) : null}
      </section>

      <HttpProxiesSection />
    </div>
  );
}

/**
 * "built-in service" with the live catalog in a tooltip — a placeholder until
 * there's a page to link to. Falls back to plain text while the catalog loads.
 *
 * Names the OFFERED providers only: an unlisted one (the direct `google`) is
 * still connectable by an App that declares it, and still renders above under
 * "Connected services", but naming it here would advertise it as something to
 * build against.
 */
function BuiltInServicesHint({
  catalog,
}: {
  catalog: IntegrationCatalogEntry[] | undefined;
}) {
  const names = (catalog ?? [])
    .filter((entry) => entry.offered && entry.configured)
    .map((entry) => entry.displayName);
  if (names.length === 0) return <>built-in service</>;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help underline decoration-dotted underline-offset-2">
            built-in service
          </span>
        </TooltipTrigger>
        <TooltipContent>{names.join(", ")}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function IntegrationRow({
  entry,
  connections,
  onDisconnect,
  disconnectingId,
  onMakeDefault,
}: {
  entry: IntegrationCatalogEntry;
  connections: IntegrationConnection[];
  onDisconnect: (connectionId: string) => void;
  disconnectingId: string | undefined;
  onMakeDefault: (connectionId: string, isDefault: boolean) => void;
}) {
  return (
    <li className="px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        {entry.logoUrl ? (
          <img
            src={entry.logoUrl}
            alt={entry.logoAlt ?? ""}
            className="h-8 w-auto shrink-0"
          />
        ) : null}
        <div className="min-w-0">
          <div className="font-medium">{entry.displayName}</div>
          <div className="text-xs text-muted-foreground">
            {connections.length === 1
              ? "1 account connected"
              : `${connections.length} accounts connected`}
          </div>
        </div>
      </div>

      <ul className="mt-3 space-y-2">
        {connections.map((connection) => (
          <AccountRow
            key={connection.id}
            connection={connection}
            // The default marker only means something once there's a choice.
            showDefaultControls={connections.length > 1}
            disconnecting={disconnectingId === connection.id}
            onDisconnect={() => onDisconnect(connection.id)}
            onMakeDefault={() =>
              onMakeDefault(connection.id, !connection.isDefault)
            }
          />
        ))}
      </ul>

      {entry.finePrint ? (
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground/80">
          {entry.finePrint}
        </p>
      ) : null}
    </li>
  );
}

function AccountRow({
  connection,
  showDefaultControls,
  disconnecting,
  onDisconnect,
  onMakeDefault,
}: {
  connection: IntegrationConnection;
  showDefaultControls: boolean;
  disconnecting: boolean;
  onDisconnect: () => void;
  onMakeDefault: () => void;
}) {
  // The account identity when the provider reported one (e.g. the Gmail
  // address); otherwise fall back to who connected it.
  const identity =
    connection.label ??
    connection.connectedBy?.name ??
    connection.connectedBy?.email ??
    "Connected account";
  const grantedScopes = connection.scopes.split(" ").filter(Boolean);
  // A connection whose refresh token has a stated deadline will stop working
  // on its own — no call, no revocation, nothing a member did. Say so BEFORE
  // it happens (and after), because the alternative is what this pane used to
  // do: render a dead account and a healthy one identically.
  const reconnectBy =
    connection.reconnectBy != null ? new Date(connection.reconnectBy) : null;

  return (
    <li className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="truncate">{identity}</span>
            {!connection.live ? (
              <span
                className="shrink-0 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive"
                title="This account's access has expired. Apps will prompt you to reconnect it when they next need it."
              >
                reconnect needed
              </span>
            ) : null}
            {connection.isDefault ? (
              <span
                className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                title="Apps and background runs use this account when no specific one is chosen."
              >
                default
              </span>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            {connection.connectedBy && connection.label
              ? `connected by ${connection.connectedBy.name ?? connection.connectedBy.email} · `
              : null}
            since {new Date(connection.connectedAt * 1000).toLocaleDateString()}
            {reconnectBy && connection.live
              ? ` · access expires ${reconnectBy.toLocaleDateString()}`
              : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showDefaultControls ? (
            <button
              type="button"
              onClick={onMakeDefault}
              title={
                connection.isDefault
                  ? "Stop using this account as the household default"
                  : "Use this account when an App doesn't pick one"
              }
              className="rounded-md border border-border p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Star
                className={`size-3.5 ${connection.isDefault ? "fill-current text-primary" : ""}`}
              />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnecting}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            {disconnecting ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
      </div>

      {grantedScopes.length > 0 ? (
        <Collapsible className="mt-1.5">
          <CollapsibleTrigger className="group flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
            <ChevronRight className="size-3 transition-transform group-data-[state=open]:rotate-90" />
            {grantedScopes.length === 1
              ? "1 granted scope"
              : `${grantedScopes.length} granted scopes`}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-1.5">
            <ul className="space-y-0.5">
              {grantedScopes.map((scope) => (
                <li
                  key={scope}
                  className="break-all font-mono text-xs text-muted-foreground"
                >
                  {scope}
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </li>
  );
}
